/**
 * Generic WebRTC Connection Manager
 * Handles WebRTC connection lifecycle independent of specific APIs
 */

import { AudioManager } from './AudioManager';
import { ConnectionStateMachine, type ConnectionEvent } from './ConnectionStateMachine';
import type { WebRTCDependencies } from './WebRTCFactory';
import type {
  ConnectionState, 
  ConnectionStats, 
  WebRTCConnectionEvents, 
  WebRTCConnectionConfig,
  InternalWebRTCConfig as WebRTCConfig,
  WebRTCProviderInterface
} from './WebRTCProvider';

/**
 * Custom signer interface for WebRTC
 */
interface ICustomSigner {
  getSignedURL: () => string;
}

/**
 * WebRTC Connection internal dependencies
 */
interface WebRTCConnectionDependencies {
  audioManager: AudioManager;
  stateMachine: ConnectionStateMachine;
  dependencies: WebRTCDependencies;
}
/**
 * Extend Window interface to include KVSWebRTC
 */
declare global {
  interface Window {
    KVSWebRTC: {
      SignalingClient: any;
      Role: {
        VIEWER: string;
        MASTER: string;
      };
    };
  }
}

/**
 * WebRTC connection manager class
 * 🔧 INTERNAL USE ONLY - Should only be accessed via WebRTCProvider
 */
export class WebRTCConnection {
  // Configuration
  private config: WebRTCConnectionConfig;
  private webrtcConfig: WebRTCConfig | null = null;
  private provider: WebRTCProviderInterface;
  private connectionId: string;
  
  // Injected dependencies
  private audioManager: AudioManager;
  private stateMachine: ConnectionStateMachine;
  private dependencies: WebRTCDependencies;
  
  // Connection state
  private error: Error | null = null;
  
  // WebRTC objects
  private peerConnection: RTCPeerConnection | null = null;
  private signalingClient: any = null;
  private stream: MediaStream | null = null;
  
  // Event deduplication
  private receivedStreams: Set<string> = new Set();
  private connectionEstablished: boolean = false;
  
  // Audio renegotiation tracking
  private audioRenegotiationInProgress: boolean = false;
  private lastOfferCreationTime: number = 0;
  
  // Statistics monitoring
  private stats: ConnectionStats | null = null;
  private statsInterval: NodeJS.Timeout | null = null;
  
  // Event handlers
  private eventHandlers: Partial<WebRTCConnectionEvents> = {};
  
  // RTP monitoring
  private rtpMonitor: NodeJS.Timeout | null = null;
  private lastAudioBytesSent = 0;
  private lastAudioBytesReceived = 0;

  /**
   * Create a new WebRTC connection instance
   * 🔧 INTERNAL USE ONLY - Called by WebRTCProvider
   * @param config - Connection configuration with provider
   * @param internalDeps - Injected dependencies for better testability
   */
  constructor(config: WebRTCConnectionConfig, internalDeps: WebRTCConnectionDependencies) {
    this.config = config;
    this.provider = config.provider;
    this.connectionId = `${config.deviceId}_${config.channel}_${Date.now()}`;
    
    // Set up injected dependencies
    this.audioManager = internalDeps.audioManager;
    this.stateMachine = internalDeps.stateMachine;
    this.dependencies = internalDeps.dependencies;
    
    // Set up state machine callbacks
    this.stateMachine.setCallbacks({
      onStateChange: (newState, previousState, event) => {
        this.eventHandlers.onStateChange?.(newState);
      },
      onInvalidTransition: (currentState, event) => {
        console.warn(`🚫 [WebRTCConnection] Invalid state transition rejected: ${currentState} + ${event}`);
      }
    });
    
    // Initialize event handlers from config
    if (config.onStateChange) this.eventHandlers.onStateChange = config.onStateChange;
    if (config.onError) this.eventHandlers.onError = config.onError;
    if (config.onStreamReceived) this.eventHandlers.onStreamReceived = config.onStreamReceived;
    if (config.onStatsUpdate) this.eventHandlers.onStatsUpdate = config.onStatsUpdate;
  }

  /**
   * Start WebRTC connection
   * Executes the complete workflow: get config -> setup connection -> start streaming
   * Assumes WebRTC SDK is already loaded by WebRTCPlayer
   */
  async connect(): Promise<void> {
    // Prevent duplicate connection attempts
    if (this.stateMachine.getState() === 'connecting') {
      return;
    }
    
    if (this.stateMachine.getState() === 'connected') {
      return;
    }

    try {
      this.stateMachine.transition('CONNECT');
      this.setError(null);


      // Verify WebRTC SDK is available
      if (!this.dependencies.webrtcSDK) {
        // Try to get SDK from window as fallback
        const webrtcSDK = typeof window !== 'undefined' ? (window as any).KVSWebRTC : null;
        if (!webrtcSDK) {
          throw new Error('WebRTC SDK not available. Please ensure SDK is loaded before creating connection.');
        }
        // Update dependencies with found SDK
        this.dependencies.webrtcSDK = webrtcSDK;
      }

      // Step 1: Use pre-obtained WebRTC config or get from provider
      if (this.config.webrtcConfig) {
        this.webrtcConfig = this.config.webrtcConfig;
      } else {
        const providerConfig = {
          deviceId: this.config.deviceId,
          channel: this.config.channel,
          clientId: this.config.clientId || this.config.deviceId,
          role: this.config.role || 'VIEWER'
        };
        this.webrtcConfig = await this.provider.getWebRTCConfig(providerConfig);
      }

      // Step 2: Initialize WebRTC connection
      await this.initializeWebRTCConnection(this.webrtcConfig);

      // Step 3: Start statistics monitoring if enabled
      if (this.config.enableStats) {
        this.startStatsMonitoring();
      }

    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Connection failed');
      this.setError(errorObj);
      this.stateMachine.transition('ERROR');
      throw errorObj;
    }
  }

  /**
   * Disconnect WebRTC connection
   * Cleans up all resources and stops streaming
   */
  async disconnect(): Promise<void> {
    try {

      // Stop statistics monitoring
      this.stopStatsMonitoring();

      // Clear stream tracking state
      this.receivedStreams.clear();
      this.connectionEstablished = false;
      this.stream = null;

      // Close peer connection
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      // Close signaling client
      if (this.signalingClient) {
        try {
          this.signalingClient.close();
        } catch (error) {
          // Ignore signaling client close errors
        }
        this.signalingClient = null;
      }

      // Stop streaming on provider
      try {
        await this.provider.stopStreaming(this.config.deviceId);
      } catch (error) {
        // Ignore provider stop errors
      }

      // Reset connection state

      this.webrtcConfig = null;
      this.stats = null;
      this.stateMachine.transition('DISCONNECT');

    } catch (error) {
      this.stateMachine.transition('DISCONNECT');
    }
  }

  /**
   * Change channel
   * @param channel - New channel to switch to
   */
  async changeChannel(channel: string): Promise<void> {
    const currentState = this.stateMachine.getState();
    if (channel === this.config.channel || currentState === 'connecting' || currentState === 'idle') {
      return;
    }

    try {
      this.stateMachine.transition('CHANNEL_CHANGE_START');
      
      // Clear stream state for channel change
      this.receivedStreams.clear();
      this.stream = null;
      
      // Use provider to change channel
      await this.provider.changeChannel(this.config.deviceId, channel);
      
      // Update local config only - no connection restart
      this.config.channel = channel;
      
      this.stateMachine.transition('CHANNEL_CHANGE_COMPLETE');
      
    } catch (error) {
      // Create user-friendly error message
      const errorObj = new Error(`Channel ${channel} is not available`);
      this.setError(errorObj);
      
      // Reset to connected state if channel change fails
      if (this.stateMachine.getState() === 'connected') {
        this.stateMachine.transition('CHANNEL_CHANGE_COMPLETE');
      }
      
      throw errorObj;
    }
  }

  /**
   * Start two-way audio communication
   */
  async startTwoWayAudio(): Promise<void> {
    if (!this.peerConnection || this.stateMachine.getState() !== 'connected') {
      throw new Error('WebRTC connection not established');
    }
    
    // Delegate to audio manager
    await this.audioManager.startTwoWayAudio(this.peerConnection);
    
    // 🚨 CRITICAL: After audio manager renegotiation, we need to send the updated offer!
    if (this.peerConnection.localDescription && this.signalingClient) {
      try {
        this.signalingClient.sendSdpOffer(this.peerConnection.localDescription);
      } catch (error) {
        throw new Error(`Failed to send audio offer: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      throw new Error('Cannot send audio offer - signaling not ready');
    }
  }
  
  /**
   * Stop two-way audio communication
   */
  async stopTwoWayAudio(): Promise<void> {
    // Delegate to audio manager
    await this.audioManager.stopTwoWayAudio(this.peerConnection || undefined);
  }
  
  /**
   * Mute/unmute local audio
   */
  setLocalAudioEnabled(enabled: boolean): void {
    this.audioManager.setLocalAudioEnabled(enabled);
  }
  
  getLocalAudioStream(): MediaStream | null {
    return this.audioManager.getLocalAudioStream();
  }
  
  /**
   * Get remote audio stream
   */
  getRemoteAudioStream(): MediaStream | null {
    return this.stream; // Use main video stream which contains all audio
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.stateMachine.getState();
  }

  /**
   * Get current WebRTC configuration
   */
  getConfig(): WebRTCConfig | null {
    return this.webrtcConfig;
  }

  /**
   * Get current connection statistics
   */
  getStats(): ConnectionStats | null {
    return this.stats;
  }

  /**
   * Get current error state
   */
  getError(): Error | null {
    return this.error;
  }

  /**
   * Register event handler
   */
  on<K extends keyof WebRTCConnectionEvents>(event: K, handler: WebRTCConnectionEvents[K]): void {
    this.eventHandlers[event] = handler;
  }

  /**
   * Unregister event handler
   */
  off<K extends keyof WebRTCConnectionEvents>(event: K, handler: WebRTCConnectionEvents[K]): void {
    if (this.eventHandlers[event] === handler) {
      delete this.eventHandlers[event];
    }
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    // Clean up audio manager
    this.audioManager.cleanup();
    
    // Stop RTP monitoring
    this.stopRTPMonitoring();
    
    this.stopStatsMonitoring();
    
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    
    if (this.signalingClient) {
      try {
        this.signalingClient.close();
      } catch (error) {
        // Ignore close errors
      }
      this.signalingClient = null;
    }
    
    // Clear event deduplication state
    this.receivedStreams.clear();
    this.connectionEstablished = false;
    
    // Don't call provider.stopStreaming in destroy() to avoid unnecessary API calls
    // Only clean up local WebRTC resources
    
    this.eventHandlers = {};
    this.webrtcConfig = null;
    this.stats = null;
    this.stateMachine.reset();
  }

  /**
   * Initialize WebRTC connection using KVS WebRTC SDK
   * @param signalingInfo - WebRTC configuration from provider
   */
  private async initializeWebRTCConnection(signalingInfo: WebRTCConfig): Promise<void> {
    try {
      // Create custom signer for presigned URL
      const customSigner: ICustomSigner = {
        getSignedURL: () => signalingInfo.url
      };

      // Step 1: Create WebRTC PeerConnection with ICE servers
      const peerConnection = new RTCPeerConnection({ 
        iceServers: signalingInfo.iceServers.map(server => ({
          urls: server.urls,
          username: server.username,
          credential: server.credential
        }))
      });
      this.peerConnection = peerConnection;

      // Step 2: Create SignalingClient instance  
      const signalingClient = new this.dependencies.webrtcSDK!.SignalingClient({
        requestSigner: customSigner,
        region: signalingInfo.region,
        role: this.dependencies.webrtcSDK!.Role.VIEWER,
        clientId: signalingInfo.clientId,
        channelARN: signalingInfo.channelARN,
        channelEndpoint: signalingInfo.endpointsByProtocol.WSS
      });
      this.signalingClient = signalingClient;

      // Step 3: Set up remote stream handling
      peerConnection.ontrack = this._handleTrackEvent.bind(this);

      // Step 4: Set up ICE connection state monitoring
      peerConnection.oniceconnectionstatechange = () => {
        const currentState = this.stateMachine.getState();
        const iceState = peerConnection.iceConnectionState;
        
        switch (iceState) {
          case 'connected':
          case 'completed':
            // Only establish connection once via ICE
            if (!this.connectionEstablished && currentState !== 'connected') {
              this.connectionEstablished = true;
              this.stateMachine.transition('CONNECTION_ESTABLISHED');
              
              // Start detailed RTP monitoring for audio packets
              this.startRTPMonitoring(peerConnection);
            }
            break;
          case 'disconnected':
            // Don't disconnect during channel changes
            if (currentState !== 'connected' || !this.stateMachine.canTransition('CHANNEL_CHANGE_START')) {
              this.connectionEstablished = false;
              this.stateMachine.transition('DISCONNECT');
            }
            break;
          case 'failed':
            // Don't fail during channel changes  
            if (currentState !== 'connected' || !this.stateMachine.canTransition('CHANNEL_CHANGE_START')) {
              this.connectionEstablished = false;
              const error = new Error('ICE connection failed');
              this.setError(error);
              this.stateMachine.transition('ERROR');
            }
            break;
          case 'closed':
            // Don't close during channel changes
            if (currentState !== 'connected' || !this.stateMachine.canTransition('CHANNEL_CHANGE_START')) {
              this.connectionEstablished = false;
              this.stateMachine.transition('DISCONNECT');
            }
            break;
        }
      };

      // Step 5: Set up SignalingClient event handlers
      
      // Handle signaling connection open
      signalingClient.on('open', async () => {
        try {
          // Create SDP Offer
          const offer = await peerConnection.createOffer({ 
            offerToReceiveAudio: true, 
            offerToReceiveVideo: true 
          });
          
          // Set local description
          await peerConnection.setLocalDescription(offer);
          
          // Send SDP offer to signaling server
          signalingClient.sendSdpOffer(peerConnection.localDescription);
          
        } catch (error) {
          const errorObj = error instanceof Error ? error : new Error('Failed to create SDP offer');
          this.setError(errorObj);
          this.stateMachine.transition('ERROR');
        }
      });

      // Handle SDP Answer from remote peer
      signalingClient.on('sdpAnswer', async (answer: any) => {
        const now = Date.now();
        const timeSinceLastOffer = now - this.lastOfferCreationTime;
        try {
          // Check if this is ACTUALLY an audio renegotiation answer
          const isActualAudioRenegotiation = this.audioRenegotiationInProgress && 
                                           timeSinceLastOffer < 10000 && // Within 10 seconds of offer
                                           peerConnection.localDescription?.type === 'offer' && 
                                           peerConnection.localDescription?.sdp?.includes('m=audio') &&
                                           answer.sdp?.includes('m=audio');
          
          await peerConnection.setRemoteDescription(answer);
          
          if (isActualAudioRenegotiation) {
            // Reset audio renegotiation flag
            this.audioRenegotiationInProgress = false;
            (peerConnection as any).__audioRenegotiationInProgress = false;
          }
        } catch (error) {
          const errorObj = error instanceof Error ? error : new Error('Failed to set remote description');
          this.setError(errorObj);
          this.stateMachine.transition('ERROR');
        }
      });

      // Handle ICE candidates from remote peer
      signalingClient.on('iceCandidate', async (candidate: any) => {
        try {
          await peerConnection.addIceCandidate(candidate);
        } catch (error) {
          // Ignore ICE candidate errors
        }
      });

      // Handle local ICE candidates
      peerConnection.onicecandidate = ({ candidate }) => {
        if (candidate) {
          signalingClient.sendIceCandidate(candidate);
        }
      };

      // Handle signaling connection close
      signalingClient.on('close', () => {
        if (!this.stateMachine.canTransition('CHANNEL_CHANGE_START')) {
          this.stateMachine.transition('DISCONNECT');
        }
      });

      // Handle signaling errors
      signalingClient.on('error', (err: any) => {
        if (!this.stateMachine.canTransition('CHANNEL_CHANGE_START')) {
          const error = new Error(`Signaling error: ${err.message}`);
          this.setError(error);
          this.stateMachine.transition('ERROR');
        }
      });

      // Step 6: Open signaling connection
      signalingClient.open();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Start connection statistics monitoring
   */
  private startStatsMonitoring(): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
    }

    const interval = this.config.statsInterval || 2000;
    
    this.statsInterval = setInterval(async () => {
      if (this.peerConnection && this.stateMachine.getState() === 'connected') {
        try {
          const stats = await this.peerConnection.getStats();
          let bytesReceived = 0;
          let packetsReceived = 0;
          let packetsLost = 0;
          let jitter = 0;
          let roundTripTime = 0;

          stats.forEach((report) => {
            if (report.type === 'inbound-rtp' && report.mediaType === 'video') {
              bytesReceived += report.bytesReceived || 0;
              packetsReceived += report.packetsReceived || 0;
              packetsLost += report.packetsLost || 0;
              jitter = report.jitter || 0;
            }
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              roundTripTime = report.currentRoundTripTime || 0;
            }
          });

          this.stats = {
            bytesReceived,
            packetsReceived,
            packetsLost,
            jitter,
            roundTripTime
          };

          this.eventHandlers.onStatsUpdate?.(this.stats);
        } catch (error) {
          // Ignore stats errors
        }
      }
    }, interval);
  }

  /**
   * Stop connection statistics monitoring
   */
  private stopStatsMonitoring(): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }
  }


  /**
   * Set error and notify handlers
   */
  private setError(error: Error | null): void {
    this.error = error;
    // Always emit error events to ensure UI can handle them
    if (error) {
      this.eventHandlers.onError?.(error);
    }
  }
  

  /**
   * Handle track event with proper context binding
   */
  private _handleTrackEvent(event: RTCTrackEvent): void {
    const trackKind = event.track?.kind || 'unknown';
    const streamCount = event.streams.length;
    
    if (event.streams[0]) {
      const streamId = event.streams[0].id;
      const stream = event.streams[0];
      const isNewStream = !this.receivedStreams.has(streamId);
      
      if (isNewStream) {
        this.receivedStreams.add(streamId);
        this.stream = stream;
        
        // Monitor received stream audio tracks
        stream.getAudioTracks().forEach((track, index) => {
        });
        
        // Notify about new stream - this will go to WebRTCProvider
        this.eventHandlers.onStreamReceived?.(stream);
        
        // Only establish connection via track if ICE hasn't done it yet
        if (!this.connectionEstablished && this.stateMachine.getState() !== 'connected') {
          this.connectionEstablished = true;
          this.stateMachine.transition('CONNECTION_ESTABLISHED');
        }
      } else {
        // Just update the stream reference, don't trigger events
        this.stream = stream;
        // Don't call onStreamReceived for additional tracks of same stream
      }
    } else {
    }
  }
  
  /**
   * Start detailed RTP packet transmission monitoring
   */
  private startRTPMonitoring(peerConnection: RTCPeerConnection): void {
    if (this.rtpMonitor) {
      clearInterval(this.rtpMonitor);
    }
    
    this.rtpMonitor = setInterval(async () => {
      try {
        const stats = await peerConnection.getStats();
        let hasActiveAudioTransmission = false;
        let hasActiveAudioReception = false;
        
        stats.forEach((report) => {
          // Monitor outbound audio (local microphone → remote)
          if (report.type === 'outbound-rtp' && report.mediaType === 'audio') {
            const bytesSent = report.bytesSent || 0;
            const packetsSent = report.packetsSent || 0;
            const bytesPerSecond = bytesSent - this.lastAudioBytesSent;
            
            if (bytesSent > this.lastAudioBytesSent) {
              hasActiveAudioTransmission = true;
            }
            
            this.lastAudioBytesSent = bytesSent;
          }
          
          // Monitor inbound audio (remote → local speakers)
          if (report.type === 'inbound-rtp' && report.mediaType === 'audio') {
            const bytesReceived = report.bytesReceived || 0;
            const packetsReceived = report.packetsReceived || 0;
            const bytesPerSecond = bytesReceived - this.lastAudioBytesReceived;
            
            if (bytesReceived > this.lastAudioBytesReceived) {
              hasActiveAudioReception = true;
            }
            
            this.lastAudioBytesReceived = bytesReceived;
          }
        });
        
        // Summary log every monitoring cycle
        if (hasActiveAudioTransmission || hasActiveAudioReception) {
        }
      } catch (error) {
      }
    }, 2000); // Monitor every 2 seconds
  }
  
  /**
   * Stop RTP monitoring
   */
  private stopRTPMonitoring(): void {
    if (this.rtpMonitor) {
      clearInterval(this.rtpMonitor);
      this.rtpMonitor = null;
    }
  }
}